import type { ReactNode, SVGProps } from 'react';

/** Małe ikony liniowe (styl Tabler) rysowane w SVG – bez dodatkowej biblioteki. Kolor z `currentColor`. */
type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function icon(paths: ReactNode) {
  return function Icon({ size = 20, ...props }: IconProps) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        {...props}
      >
        {paths}
      </svg>
    );
  };
}

export const IconHome = icon(
  <>
    <path d="M5 12H3l9-9 9 9h-2" />
    <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
    <path d="M9 21v-6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v6" />
  </>,
);
export const IconCalendar = icon(
  <>
    <rect x="4" y="5" width="16" height="16" rx="2" />
    <path d="M16 3v4M8 3v4M4 11h16M8 15h2v2H8z" />
  </>,
);
export const IconUsers = icon(
  <>
    <circle cx="9" cy="7" r="4" />
    <path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75M21 21v-2a4 4 0 0 0-3-3.85" />
  </>,
);
export const IconUser = icon(
  <>
    <circle cx="12" cy="7" r="4" />
    <path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
  </>,
);
export const IconSchool = icon(
  <>
    <path d="M22 9 12 5 2 9l10 4 10-4v6" />
    <path d="M6 10.6V16a6 3 0 0 0 12 0v-5.4" />
  </>,
);
export const IconQrcode = icon(
  <>
    <rect x="4" y="4" width="6" height="6" rx="1" />
    <rect x="14" y="4" width="6" height="6" rx="1" />
    <rect x="4" y="14" width="6" height="6" rx="1" />
    <path d="M14 14h2v2h-2zM18 18h2v2h-2zM14 18v2M18 14h2M7 7v.01M17 7v.01M7 17v.01" />
  </>,
);
export const IconClipboardCheck = icon(
  <>
    <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
    <rect x="9" y="3" width="6" height="4" rx="2" />
    <path d="m9 14 2 2 4-4" />
  </>,
);
export const IconSearch = icon(
  <>
    <circle cx="10" cy="10" r="7" />
    <path d="m21 21-6-6" />
  </>,
);
export const IconAdjustments = icon(
  <>
    <path d="M4 6h8M16 6h4M4 12h2M10 12h10M4 18h11M19 18h1" />
    <circle cx="14" cy="6" r="2" />
    <circle cx="8" cy="12" r="2" />
    <circle cx="17" cy="18" r="2" />
  </>,
);
/** Koło zębate – ogólne ustawienia. */
export const IconSettings = icon(
  <>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2v3M12 19v3M4.93 4.93l2.12 2.12M16.95 16.95l2.12 2.12M2 12h3M19 12h3M4.93 19.07l2.12-2.12M16.95 7.05l2.12-2.12" />
  </>,
);
export const IconTag = icon(
  <>
    <path d="M7.5 7.5h.01" />
    <path d="M3 6v5.17a2 2 0 0 0 .59 1.42l7.71 7.71a2.41 2.41 0 0 0 3.41 0l5.17-5.17a2.41 2.41 0 0 0 0-3.41l-7.71-7.71A2 2 0 0 0 10.76 3H6a3 3 0 0 0-3 3z" />
  </>,
);
export const IconShieldCheck = icon(
  <>
    <path d="M12 3a12 12 0 0 0 8.5 3A12 12 0 0 1 12 21 12 12 0 0 1 3.5 6 12 12 0 0 0 12 3" />
    <path d="m9 12 2 2 4-4" />
  </>,
);
export const IconSpeakerphone = icon(
  <>
    <path d="M18 8a3 3 0 0 1 0 6" />
    <path d="M10 8v11a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1v-5" />
    <path d="m12 8 4.52-3.77A.9.9 0 0 1 18 4.92v12.16a.9.9 0 0 1-1.48.69L12 14H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1h8" />
  </>,
);
export const IconMail = icon(
  <>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </>,
);
/** Kartka z tekstem przy dolnej krawędzi – stopka maili. */
export const IconMailFooter = icon(
  <>
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <path d="M8 7h8M8 11h8M4 16h16M8 18.5h5" />
  </>,
);
/** Uścisk dłoni w uproszczeniu – dwie splecione pętle (partnerzy wydarzenia). */
export const IconPartners = icon(
  <>
    <circle cx="9" cy="12" r="5" />
    <circle cx="15" cy="12" r="5" />
  </>,
);
export const IconLogin = icon(
  <>
    <path d="M10 8V6a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-7a2 2 0 0 1-2-2v-2" />
    <path d="M3 12h12l-3-3M12 15l3-3" />
  </>,
);
export const IconLogout = icon(
  <>
    <path d="M14 8V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2v-2" />
    <path d="M9 12h12l-3-3M18 15l3-3" />
  </>,
);
export const IconMapPin = icon(
  <>
    <circle cx="12" cy="11" r="3" />
    <path d="M17.66 16.66 13.41 20.9a2 2 0 0 1-2.83 0l-4.24-4.24a8 8 0 1 1 11.32 0z" />
  </>,
);
export const IconClock = icon(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 3" />
  </>,
);
/** Trasa: punkt startu, kręta droga, pinezka celu (mapa dojazdu). */
export const IconRoute = icon(
  <>
    <circle cx="6" cy="19" r="2" />
    <path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H13" />
    <path d="M19 9.5c-1.3-1.4-2-2.6-2-3.5a2 2 0 1 1 4 0c0 .9-.7 2.1-2 3.5z" />
  </>,
);
/** Rzut piętra: ściany z drzwiami (plan budynku). */
export const IconFloorPlan = icon(
  <>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 12h6M13 12h8M12 3v5M12 12v9M16 12v-2" />
  </>,
);
export const IconExternalLink = icon(
  <>
    <path d="M12 6H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />
    <path d="M11 13l9-9M15 4h5v5" />
  </>,
);
/** Dokument z liniami tekstu (materiały edycji). */
export const IconFileText = icon(
  <>
    <path d="M14 3v4a1 1 0 0 0 1 1h4" />
    <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
    <path d="M9 13h6M9 17h6M9 9h1" />
  </>,
);
/** Dokument ze strzałką do środka (import agendy). */
export const IconFileImport = icon(
  <>
    <path d="M14 3v4a1 1 0 0 0 1 1h4" />
    <path d="M5 13V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2h-5.5" />
    <path d="M2 19h7M6 16l3 3-3 3" />
  </>,
);
export const IconInfoCircle = icon(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8h.01M11 12h1v4h1" />
  </>,
);
export const IconChevronDown = icon(<path d="m6 9 6 6 6-6" />);
export const IconChevronRight = icon(<path d="m9 6 6 6-6 6" />);
export const IconSparkles = icon(
  <path d="M16 18a2 2 0 0 1 2 2 2 2 0 0 1 2-2 2 2 0 0 1-2-2 2 2 0 0 1-2 2zm0-12a2 2 0 0 1 2 2 2 2 0 0 1 2-2 2 2 0 0 1-2-2 2 2 0 0 1-2 2zM9 18a6 6 0 0 1 6-6 6 6 0 0 1-6-6 6 6 0 0 1-6 6 6 6 0 0 1 6 6z" />,
);
export const IconCode = icon(<path d="m7 8-4 4 4 4M17 8l4 4-4 4M14 4l-4 16" />);
export const IconRobot = icon(
  <>
    <rect x="6" y="4" width="12" height="8" rx="2" />
    <path d="M12 2v2M9 12v9M15 12v9M5 16l4-2M15 14l4 2M9 18h6M10 8v.01M14 8v.01" />
  </>,
);
export const IconBulb = icon(
  <path d="M3 12h1m8-9v1m8 8h1M5.6 5.6l.7.7m12.1-.7-.7.7M9 16a5 5 0 1 1 6 0 3.5 3.5 0 0 0-1 3 2 2 0 0 1-4 0 3.5 3.5 0 0 0-1-3M9.7 17h4.6" />,
);
export const IconPencil = icon(
  <>
    <path d="M4 20h4L18.5 9.5a2.83 2.83 0 0 0-4-4L4 16v4" />
    <path d="m13.5 6.5 4 4" />
  </>,
);
export const IconTrash = icon(
  <>
    <path d="M4 7h16M10 11v6M14 11v6" />
    <path d="m5 7 1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3" />
  </>,
);
export const IconCalendarCheck = icon(
  <>
    <path d="M11.5 21H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v6" />
    <path d="M16 3v4M8 3v4M4 11h16M15 19l2 2 4-4" />
  </>,
);
export const IconChartBar = icon(<path d="M3 21h18M6 21V12M11 21V6M16 21v-8M21 21V9" />);
export const IconCheck = icon(<path d="m5 12 5 5L20 7" />);
export const IconTable = icon(
  <>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M3 10h18M9 4v16M15 4v16" />
  </>,
);
export const IconLayoutCards = icon(
  <>
    <rect x="4" y="4" width="7" height="7" rx="1.5" />
    <rect x="13" y="4" width="7" height="7" rx="1.5" />
    <rect x="4" y="13" width="7" height="7" rx="1.5" />
    <rect x="13" y="13" width="7" height="7" rx="1.5" />
  </>,
);
export const IconFileCheck = icon(
  <>
    <path d="M14 3v4a1 1 0 0 0 1 1h4" />
    <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
    <path d="m9 15 2 2 4-4" />
  </>,
);
export const IconMessage = icon(
  <>
    <path d="M8 9h8M8 13h6" />
    <path d="M18 4a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3h-5l-5 3v-3H6a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h12z" />
  </>,
);
export const IconUserPlus = icon(
  <>
    <circle cx="9" cy="7" r="4" />
    <path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2M16 11h6M19 8v6" />
  </>,
);
